import { must } from './dom.ts';
import type { AssignmentConfig, Message, SiteMetadata } from './evaluator-types.ts';
import replize, { type Repl } from './repl.ts';

/*
 * The evaluation iframe's window, as seen from out here: the globals we
 * install for code running in the iframe (student code, harness scripts) to
 * talk back through.
 */
type EvalWindow = Window & {
  repl: Repl;
  console: Repl['console'];
  minibuffer: { message: Message };
  GITHUB_USER: string;
  GITHUB_ORG: string | undefined;
  BRANCH: string;
  javascriptEvaluator: JavascriptEvaluator;
  __bhsModuleDone: (error: unknown) => void;
};

const DEFAULT_IFRAME_CONFIG = {
  hidden: true,
  src: 'about:blank',
};

/*
 * Multi-file support: tab modules are served from blob: URLs, and a relative
 * specifier like './helper.js' can't resolve against a blob: base. So before
 * blobbing we normalize any specifier that names one of the tabs ('./helper.js',
 * 'helper.js', './helper', 'helper') to the bare filename, which the import
 * map maps to the tab's blob URL. Other specifiers pass through untouched.
 *
 * The rewriting is textual (static import/export from-clauses, side-effect
 * imports, and dynamic import() with a string literal), which could in
 * principle also match inside a string or comment that mimics an import —
 * acceptable for student code in exchange for not shipping a JS parser.
 */
const SPECIFIER_PATTERNS = [
  /(\bfrom\s*)(["'])([^"']+)\2/g, // import ... from 'x' / export ... from 'x'
  /(\bimport\s*)(["'])([^"']+)\2/g, // import 'x'
  /(\bimport\s*\(\s*)(["'])([^"']+)\2/g, // import('x')
];

const rewriteSpecifiers = (content: string, names: Set<string>) => {
  const canonical = (spec: string) => {
    let s = spec.startsWith('./') ? spec.slice(2) : spec;
    if (!s.endsWith('.js')) s += '.js';
    return names.has(s) ? s : null;
  };
  return SPECIFIER_PATTERNS.reduce(
    (text, pattern) =>
      text.replace(pattern, (whole, before, quote, spec) => {
        const name = canonical(spec);
        return name === null ? whole : `${before}${quote}${name}${quote}`;
      }),
    content,
  );
};

/*
 * The placeholder for where we will add the first evaluation iframe. If the
 * document contains an iframe element we use it. Otherwise, just stick an empty
 * div at the end of the body which will be immediately replaced.
 */
const placeholder = (): Element => {
  let ph: Element | null = document.querySelector('iframe');
  if (!ph) {
    ph = document.createElement('div');
    document.body.appendChild(ph);
  }
  return ph;
};

class JavascriptEvaluator {
  config: AssignmentConfig & { iframe: Record<string, string | boolean> };
  scriptConfig: Record<string, string>;
  repl: Repl;
  message: Message;
  username: string;
  iframe: HTMLIFrameElement | null;
  error: string | null;
  fromRepl: boolean;
  getResults: ((config: AssignmentConfig) => unknown) | undefined;
  metadata: SiteMetadata;

  constructor(
    config: AssignmentConfig,
    message: Message,
    username: string,
    metadata: SiteMetadata,
  ) {
    this.config = { ...config, iframe: config.iframe ?? DEFAULT_IFRAME_CONFIG };
    // FIXME: none of the config.json files in the original ItP contain a
    // 'script' property so it's not clear this does anything. It looks like
    // it's used to set attributes on the <script> tag but I'm not sure when I
    // needed to do that.
    this.scriptConfig = config.script ?? {};
    this.repl = replize(must(config.repl, 'repl id in config'));
    this.repl.evaluate = (code, source) => this.evaluate(code, source);
    this.message = message;
    this.username = username;
    this.iframe = null;
    this.error = null;
    this.fromRepl = false;
    this.getResults = undefined;
    this.metadata = metadata;

    // Now reset things.
    this.resetIframe();
  }

  /*
   * Evaluate code in the current iframe. The code can use the objects and
   * functions attached to the the iframe in resetIframe to communicate back.
   * This method does not reset the iframe because it is also used by the repl
   * to evaluate expressions in the context of the code loaded from the editor.
   */
  // The iframe (and its document) exist from the constructor's resetIframe
  // on; these fail loudly if that invariant is ever broken.
  #frame(): HTMLIFrameElement {
    return must(this.iframe, 'evaluation iframe');
  }

  #doc(): Document {
    return must(this.#frame().contentDocument, 'evaluation iframe document');
  }

  evaluate(code: string, source: string) {
    this.fromRepl = source === 'repl';
    const d = this.#doc();
    const s = d.createElement('script');
    s.classList.add('injected');
    Object.entries(this.scriptConfig).forEach(([k, v]) => {
      s.setAttribute(k, v);
    });
    s.append(d.createTextNode(`"use strict";\n//# sourceURL=${source}\n${code}\n`));
    d.documentElement.append(s);
  }

  loadFile(file: string) {
    return new Promise<void>((resolve, reject) => {
      const d = this.#doc();
      const s = d.createElement('script');
      s.src = file;
      s.onload = () => {
        resolve();
      };
      s.onerror = (e) => {
        console.log(`Error loading ${file}.`);
        reject(e);
      };
      d.documentElement.append(s);
    });
  }

  /*
   * Returns a promise of the result of executing the code in the editor. It is
   * up to the code that calls execute to know what to do with the result.
   *
   * Single-file pages get the historical semantics: the code is injected as a
   * classic script into the iframe's global scope. Multi-file-capable pages
   * (more than one configured file, or allowNewFiles) instead run the files as
   * real ES modules: each tab can `import` from the others, and the main
   * file's exports are copied onto the iframe's window so the REPL and any
   * test harness can see them. Export what you want visible.
   */
  async execute(code: string, source: string, others: { file: string; content: string }[] = []) {
    const multiFile = (this.config.files?.length ?? 1) > 1 || this.config.allowNewFiles;
    return multiFile
      ? this.#executeAsModules(code, source, others)
      : this.#executeClassic(code, source);
  }

  /*
   * Load config.extraFiles (harness scripts like run-tests.js) into the
   * iframe. FIXME: should probably do something if loading any of these
   * produces an error.
   */
  #loadExtraFiles(): Promise<unknown> {
    if (this.config.extraFiles) {
      return Promise.all(
        this.config.extraFiles.map((f: string) =>
          this.loadFile(new URL(f, window.location.href).pathname),
        ),
      );
    }
    return Promise.resolve();
  }

  /*
   * If there was no error, resolve with the value of getResults. In order to
   * get results from within the context of the iframe, one of the files just
   * loaded (or I guess the user code) can install a function into the outer
   * window (via window.parent) named getResults that takes the page config
   * and does whatever it does to return "the value" of the execution. If no
   * such function has been installed the value will be undefined which is
   * fine.
   *
   * In the case of the unit tests, the run-tests.js script installs a
   * getResults that runs a bunch of test cases and returns a blob of data
   * about the results.
   */
  #settle(resolve: (v: unknown) => void, reject: (e: unknown) => void) {
    if (!this.config.iframe.hidden) {
      this.#frame().focus();
    }
    // If there was an error this.error will be set by the onerror function we
    // install on the iframe's window. It is reset to null in resetIframe so
    // if it's null here, that means there was no error.
    if (this.error === null) {
      try {
        resolve(this.getResults?.(this.config));
      } catch (e) {
        console.log('Exception getting results');
        console.log(e);
      }
    } else {
      reject(this.error);
    }
  }

  async #executeClassic(code: string, source: string) {
    return new Promise((resolve, reject) => {
      this.resetIframe(() => {
        this.#loadExtraFiles().then(() => {
          this.evaluate(`${code}\nminibuffer.message('Loaded.', 1000);`, source);
          this.#settle(resolve, reject);
        });
      });
    });
  }

  /*
   * Run the editor tabs as ES modules: each file becomes a blob: URL module,
   * an import map maps tab filenames to those URLs (so `import ... from
   * './helper.js'` in any tab finds the helper tab, cycles and all, with no
   * dependency-ordering on our part), and a bootstrap module imports the main
   * file and copies its exports onto the iframe's window for the REPL and
   * getResults-style harnesses.
   */
  async #executeAsModules(
    code: string,
    source: string,
    others: { file: string; content: string }[],
  ) {
    return new Promise((resolve, reject) => {
      this.resetIframe(() => {
        this.#loadExtraFiles().then(() => {
          const d = this.#doc();
          const w = must(this.#frame().contentWindow, 'evaluation iframe window') as EvalWindow;

          const names = new Set([source, ...others.map((o) => o.file)]);
          const blobUrl = (file: string, content: string) =>
            URL.createObjectURL(
              // The sourceURL comment names the module in stack traces and
              // devtools; blob: URLs are unreadable.
              new Blob([`${rewriteSpecifiers(content, names)}\n//# sourceURL=${file}`], {
                type: 'text/javascript',
              }),
            );

          const imports: Record<string, string> = {};
          for (const { file, content } of others) {
            imports[file] = blobUrl(file, content);
          }
          imports[source] = blobUrl(source, code);

          // The import map must be in the document before any module loads.
          const im = d.createElement('script');
          im.type = 'importmap';
          im.textContent = JSON.stringify({ imports });
          d.documentElement.append(im);

          w.__bhsModuleDone = (error: unknown) => {
            Object.values(imports).forEach((u) => URL.revokeObjectURL(u));
            if (error) {
              let e = String(error);
              // An import of something that isn't a tab fails resolution
              // against the blob: base with a cryptic browser message;
              // translate it into student terms.
              const m = e.match(/Failed to resolve module specifier ["']?([^"' ]+)/);
              if (m) {
                e = `Cannot find module '${m[1]}' — there is no file with that name in this assignment.`;
              }
              this.message(e, undefined, 'error');
              this.error = e;
            }
            this.#settle(resolve, reject);
          };

          // Module evaluation is async, so completion is signalled back via
          // __bhsModuleDone rather than checked inline. Errors anywhere in
          // the module graph (including syntax errors) reject the import()
          // and land in the catch.
          const boot = d.createElement('script');
          boot.type = 'module';
          boot.textContent = `
            try {
              const main = await import(${JSON.stringify(source)});
              Object.assign(window, main);
              minibuffer.message('Loaded.', 1000);
              window.__bhsModuleDone(null);
            } catch (e) {
              window.__bhsModuleDone(e);
            }`;
          d.documentElement.append(boot);
        });
      });
    });
  }

  /*
   * Create a new iframe to use for evaluating code, evaluating a passed-in
   * function after it is fully loaded.
   */
  resetIframe(after?: () => void) {
    const f = document.createElement('iframe');

    // Depending on whether the iframe is purely local (i.e. about:blank) or
    // actually loaded over the network, the load event may fire as soon as we
    // add it to the document or at some later point. But we don't want to run
    // the after callback until all the setup in this function is complete.
    // Which is exactly what queueMicrotask is for, it seems. So here we are.
    f.onload = () => {
      queueMicrotask(() => this.repl.restart());
      if (after) {
        queueMicrotask(after);
      }
    };

    Object.entries(this.config.iframe).forEach(([k, v]) => {
      f.setAttribute(k, String(v));
    });

    (this.iframe ?? placeholder()).replaceWith(f);

    // Setup we can only do after adding the iframe to the document because
    // contentWindow doesn't exist until then. Add objects and functions to the
    // iframe's window object so code evaluated in the iframe can access them.
    const w = must(f.contentWindow, 'evaluation iframe window') as EvalWindow;
    w.repl = this.repl;
    w.console = this.repl.console;
    w.minibuffer = { message: this.message };
    w.onerror = (msg, source, line, column, error) =>
      this.showError(msg, source, line, column, error);

    // These next three are just for the advent-of-code playground for the
    // moment, I think. (It helps out running tests against the test data by
    // fetching it from github and thus needs to know these things.) But it
    // seems like it might be generally handy.
    w.GITHUB_USER = this.username;
    w.GITHUB_ORG = this.metadata.githubOrganization;
    w.BRANCH = window.location.pathname;

    w.javascriptEvaluator = this;

    this.iframe = f;
    this.error = null;
  }

  /*
   * Show errors from evaluating code.
   */
  showError(
    _msg: Event | string,
    source?: string,
    line?: number,
    column?: number,
    error?: unknown,
  ) {
    // FIXME: (6/2023) as far as I can tell from the bug report this is fixed.
    // Should take this out and check. Unfortunately I don't recall exactly what
    // the symptom was.

    // This seems to be a Chrome bug. Doesn't always happen but probably safe to
    // filter this message.
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1328008
    // https://stackoverflow.com/questions/72396527/evalerror-possible-side-effect-in-debug-evaluate-in-google-chrome
    if (error === 'EvalError: Possible side-effect in debug-evaluate') {
      return;
    }

    const e = errorMessage(error, line, column, source);

    if (this.fromRepl) {
      this.repl.error(e);
    } else {
      this.message(e, undefined, 'error');
      this.error = e;
    }
  }
}

const errorMessage = (
  error: unknown,
  line: number | undefined,
  column: number | undefined,
  source: string | undefined,
) =>
  source === 'repl'
    ? String(error)
    : `${error} (line ${Number(line) - 2}, column ${column}) of ${source}`;

const evaluator = (
  config: AssignmentConfig,
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new JavascriptEvaluator(config, message, username, metadata);

export default evaluator;
